import { createFileRoute, Link } from '@tanstack/react-router';
import type { LeadStatus } from '@pirouette/shared';
import { leadStatusSchema } from '@pirouette/shared';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { useLeads } from '@/data/leads';

export const Route = createFileRoute('/admin/leads/')({
  component: LeadsIndex,
});

const statusLabels: Record<LeadStatus, string> = {
  inquiry: 'Inquiry',
  trial_booked: 'Trial booked',
  trial_attended: 'Trial attended',
  consult_needed: 'Consult needed',
  waiver_pending: 'Waiver pending',
  payment_pending: 'Payment pending',
  enrolled: 'Enrolled',
  lost: 'Lost',
};

const statusTones: Record<LeadStatus, ComponentProps<typeof Badge>['tone']> = {
  inquiry: 'info',
  trial_booked: 'accent',
  trial_attended: 'success',
  consult_needed: 'warn',
  waiver_pending: 'warn',
  payment_pending: 'warn',
  enrolled: 'success',
  lost: 'neutral',
};

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

function LeadsIndex() {
  const [status, setStatus] = useState<LeadStatus | ''>('');
  const leads = useLeads(status || undefined);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Enrollment pipeline
          </p>
          <h1 className="mt-2">Leads</h1>
        </div>
        <label className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          Status
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as LeadStatus | '')}
            className="mt-1 block h-10 min-w-48 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-[var(--color-ink)]"
          >
            <option value="">All statuses</option>
            {leadStatusSchema.options.map((option) => (
              <option key={option} value={option}>
                {statusLabels[option]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {leads.isLoading && <p aria-live="polite">Loading…</p>}

      {leads.data?.length === 0 && (
        <Empty
          title="No leads yet"
          description="New inquiries, free trials, and enrollment follow-ups will appear here."
        />
      )}

      {leads.data && leads.data.length > 0 && (
        <div role="region" aria-label="Leads table" className="surface overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-[var(--color-surface-sunken)] text-[var(--text-sm)]">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">
                  Guardian
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Dancer
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Desired program
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Source
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Created
                </th>
                <th scope="col" className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {leads.data.map((lead) => (
                <tr
                  key={lead.id}
                  className="border-t border-[var(--color-border)] hover:bg-[var(--color-surface-sunken)]"
                >
                  <td className="px-4 py-3">
                    <span className="font-medium">{lead.guardian_name}</span>
                    <span className="block text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      {lead.guardian_email}
                    </span>
                  </td>
                  <td className="px-4 py-3">{lead.dancer_name}</td>
                  <td className="px-4 py-3">
                    <Badge tone={statusTones[lead.lead_status]}>
                      {statusLabels[lead.lead_status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-ink-muted)]">
                    {lead.program_offerings?.name ?? 'Not selected'}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-ink-muted)]">
                    {lead.source ?? 'Unknown'}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-ink-muted)]">
                    {formatDate(lead.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to="/admin/leads/$id"
                      params={{ id: lead.id }}
                      className="text-[var(--color-accent)] underline-offset-2 hover:underline"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
